import React, {FC, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {PlusOutlined} from "@ant-design/icons";
import {Alert, App, Button, Select, Skeleton, Space, Typography} from "antd";
import {DeletePopconfirm} from "../../../Components/DeleteConfirmation";
import {Panel, StatusTag} from "../../../Components/ui";
import {getUserFacingErrorMessage} from "../../../userFacingErrorUtils";
import {getEventAccessGrantLabel, getEventAccessUserLabel} from "./eventConfigPayloads";
import {
    EventAccessGrantRow,
    EventAccessUserRow
} from "./eventConfigQueries";
import {EventConfigScope} from "./useEventConfigScope";
import {
    useEventAccessGrantsForConfigQuery,
    useEventAccessUsersForConfigQuery,
    useGrantEventAccessForConfigMutation,
    useRevokeEventAccessForConfigMutation
} from "../../../GraphQL/generated/graphql";

const {Text} = Typography;

export const EventAccessPanel: FC<{scope: EventConfigScope}> = ({scope: {eventNumericId, errorLabels}}) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const [selectedAccessUserId, setSelectedAccessUserId] = useState<string>();
    const eventAccessGrantsQuery = useEventAccessGrantsForConfigQuery({
        variables: eventNumericId ? {eventId: eventNumericId} : undefined,
        skip: !eventNumericId,
        fetchPolicy: "cache-and-network"
    });
    const eventAccessUsersQuery = useEventAccessUsersForConfigQuery({
        variables: eventNumericId ? {eventId: eventNumericId, take: 100} : undefined,
        skip: !eventNumericId,
        fetchPolicy: "cache-and-network"
    });
    const [grantEventAccess, {loading: grantingEventAccess}] = useGrantEventAccessForConfigMutation();
    const [revokeEventAccess, {loading: revokingEventAccess}] = useRevokeEventAccessForConfigMutation();

    const eventAccessGrants = useMemo(
        () => (eventAccessGrantsQuery.data?.eventAccessGrants?.items ?? [])
            .filter((item): item is EventAccessGrantRow => !!item),
        [eventAccessGrantsQuery.data?.eventAccessGrants?.items]
    );
    const grantedAccessUserIds = useMemo(
        () => new Set(eventAccessGrants.map(grant => grant.userId)),
        [eventAccessGrants]
    );
    const eventAccessUserOptions = useMemo(
        () => (eventAccessUsersQuery.data?.eventAccessUsers?.items ?? [])
            .filter((item): item is EventAccessUserRow => !!item?.id && !grantedAccessUserIds.has(item.id))
            .map(user => ({
                label: getEventAccessUserLabel(user),
                value: user.id
            })),
        [eventAccessUsersQuery.data?.eventAccessUsers?.items, grantedAccessUserIds]
    );

    const handleGrantEventAccess = async () => {
        if (!eventNumericId || !selectedAccessUserId) {
            return;
        }

        try {
            await grantEventAccess({
                variables: {
                    input: {
                        eventId: eventNumericId,
                        userId: selectedAccessUserId
                    }
                }
            });
            setSelectedAccessUserId(undefined);
            message.success(t("event-access-granted", {defaultValue: "Event access granted"}));
            await eventAccessGrantsQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    const handleRevokeEventAccess = async (userId: string) => {
        if (!eventNumericId || !userId) {
            return;
        }

        try {
            await revokeEventAccess({
                variables: {
                    input: {
                        eventId: eventNumericId,
                        userId
                    }
                }
            });
            message.success(t("event-access-revoked", {defaultValue: "Event access revoked"}));
            await eventAccessGrantsQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    const loading = (eventAccessGrantsQuery.loading || eventAccessUsersQuery.loading) && eventAccessGrants.length === 0;

    return (
        <Panel title={t("event-access-card-title", {defaultValue: "Event access"})}>
            {loading ? <Skeleton active/> : (
                <>
                    {(eventAccessGrantsQuery.error || eventAccessUsersQuery.error) && (
                        <Alert
                            type="error"
                            showIcon
                            style={{marginBottom: 16}}
                            message={t("error-loading-items", {defaultValue: "Error loading items"})}
                            description={getUserFacingErrorMessage(eventAccessGrantsQuery.error ?? eventAccessUsersQuery.error, t, errorLabels)}
                        />
                    )}
                    <div className="ati-stack ati-stack--compact">
                        <Space.Compact block>
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                placeholder={t("event-access-user-placeholder", {defaultValue: "Organizer"})}
                                value={selectedAccessUserId}
                                options={eventAccessUserOptions}
                                disabled={!eventNumericId || eventAccessUsersQuery.loading}
                                onChange={value => setSelectedAccessUserId(value)}
                                style={{minWidth: 280, flex: 1}}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined/>}
                                disabled={!eventNumericId || !selectedAccessUserId}
                                loading={grantingEventAccess}
                                onClick={handleGrantEventAccess}
                            >
                                {t("event-access-grant", {defaultValue: "Grant access"})}
                            </Button>
                        </Space.Compact>
                        {eventAccessGrants.length > 0 ? (
                            <div className="ati-stack ati-stack--tight">
                                {eventAccessGrants.map(grant => (
                                    <Space key={grant.id} wrap>
                                        <StatusTag tone="info">{getEventAccessGrantLabel(grant)}</StatusTag>
                                        <DeletePopconfirm
                                            itemType={t("delete-target-event-access", {defaultValue: "event access"})}
                                            itemName={getEventAccessGrantLabel(grant)}
                                            onConfirm={() => handleRevokeEventAccess(grant.userId)}
                                        >
                                            <Button
                                                danger
                                                size="small"
                                                loading={revokingEventAccess}
                                            >
                                                {t("event-access-revoke", {defaultValue: "Revoke"})}
                                            </Button>
                                        </DeletePopconfirm>
                                    </Space>
                                ))}
                            </div>
                        ) : (
                            <Text type="secondary">{t("event-access-empty", {defaultValue: "No collaborators"})}</Text>
                        )}
                    </div>
                </>
            )}
        </Panel>
    );
};
